import { container } from '../context/module';
import { MetricsService } from '../services/metrics.service';
import { transferTray } from '../services/transfer-tray.service';
import { MetricEventName } from '../types/metric-events';

/**
 * Arrive-with-file: pick up a file the previous page stashed and stage it here.
 *
 * A tool page reached through a suggestion, a session-file hand-off or a next-step chip carries
 * `?xfer=<id>`; the bytes are in IndexedDB. This reads them, hands them to the page's own file
 * staging, and clears the pointer.
 *
 * Shared rather than per-page because it was not. `FileConvertComponent` had the only copy, and
 * every tool that does not route through it silently dropped the file — `/image-resizer` renders
 * `ImageResizeRootComponent`, so chaining to "Resize Image" carried the file, navigated, and
 * landed on an empty uploader with no error and no explanation. A second copy would have been
 * the same bug waiting on the next root.
 *
 * Everything here is best-effort: a missing, expired or foreign record leaves the user on a
 * clean tool page, which is the pre-existing behaviour, never an error.
 */
export interface PickupOptions {
    /** Extensions this page accepts. Empty or absent means it takes anything. */
    acceptedExtensions?: string[];
    /** Slug for telemetry. */
    pageSlug?: string;
    /** Hand the files to the page's normal selection path — never a bespoke upload. */
    stage: (files: File[]) => void;
}

export function pickUpTransferredFiles(opts: PickupOptions): void {
    try {
        if (typeof window === 'undefined') return;
        const params = new URLSearchParams(window.location.search);
        const xferId = params.get('xfer');
        if (!xferId) return;

        // Strip the pointer immediately so a reload or a shared URL is a clean tool page.
        params.delete('xfer');
        const qs = params.toString();
        window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));

        transferTray.take(xferId).then(record => {
            if (!record || !record.files?.length) {
                // Expired, cleared, or a different browser. They still get a usable page; we
                // only record that the file did not make it.
                emit(MetricEventName.FILE_HANDOFF_MISSED, { xferId });
                return;
            }
            // Re-validate against THIS page: the suggestion could be stale, and staging a file
            // this tool cannot take would just produce a failed conversion.
            const accepted = opts.acceptedExtensions;
            const usable = accepted?.length
                ? record.files.filter(f => accepted.includes((f.name.split('.').pop() || '').toLowerCase()))
                : record.files;
            if (!usable.length) {
                emit(MetricEventName.FILE_HANDOFF_MISSED, { xferId, reason: 'not-accepted-here' });
                return;
            }
            opts.stage(usable);
            emit(MetricEventName.FILE_HANDOFF_COMPLETED, {
                xferId, fromPage: record.fromSlug || '', fileCount: usable.length,
            });
        }).catch(() => { /* best-effort */ });
    } catch { /* best-effort */ }
}

function emit(event: MetricEventName, attrs: Record<string, unknown>) {
    try {
        container.get(MetricsService).emit(event, attrs as any);
    } catch { /* telemetry is never load-bearing */ }
}
